import { useEffect, useState } from 'react';

import { trackerApi } from '@/tracker/api/trackerApi';
import { SEARCH_PAGE_SIZE } from '@/tracker/hooks/useServerSearch';
import { Queue, Sprint, TaskPriority, TaskStatus, TaskType } from '@/tracker/types';

/**
 * Справочник грузится один раз на вкладку и делится между экранами.
 * Ошибка — пустой список (плашка просто без вариантов), следующее открытие экрана попробует снова
 */
const cached = <T>(load: () => Promise<T[]>) => {
  let request: Promise<T[]> | null = null;

  return () => {
    if (!request) {
      request = load().catch(() => {
        request = null;

        return [];
      });
    }

    return request;
  };
};

const loadPriorities = cached(async () =>
  // Чем больше sort, тем важнее — важные сверху
  [...(await trackerApi.listPriorities())].sort((a, b) => b.sort - a.sort),
);

const loadTaskTypes = cached(() => trackerApi.listTaskTypes());

const loadStatuses = cached(async () =>
  [...(await trackerApi.listStatuses())].sort((a, b) => a.sort - b.sort),
);

const useDictionary = <T>({ load, enabled }: { load: () => Promise<T[]>; enabled: boolean }) => {
  const [items, setItems] = useState<T[]>([]);

  useEffect(() => {
    if (!enabled) return;

    let isActual = true;

    load().then((loaded) => isActual && setItems(loaded));

    return () => {
      isActual = false;
    };
  }, [load, enabled]);

  return items;
};

export const usePriorities = (enabled: boolean): TaskPriority[] =>
  useDictionary({ load: loadPriorities, enabled });

export const useTaskTypes = (enabled: boolean): TaskType[] =>
  useDictionary({ load: loadTaskTypes, enabled });

/** Все статусы в порядке sort */
export const useStatuses = (enabled: boolean): TaskStatus[] =>
  useDictionary({ load: loadStatuses, enabled });

/** Спринты всех досок по названию, свежие сверху. Ошибка — пустой список */
export const searchSprints = async (query: string): Promise<Sprint[]> => {
  try {
    const page = await trackerApi.searchSprints({
      boardId: '-',
      request: { query: query.trim() || undefined, pageSize: SEARCH_PAGE_SIZE },
    });

    return page.items;
  } catch {
    return [];
  }
};

/** Доступные очереди — подсказки сервера: начало ключа или названия, мои первыми. Ошибка — пусто */
export const searchQueues = async (query: string): Promise<Queue[]> => {
  try {
    return await trackerApi.suggestQueues({
      query: query.trim() || undefined,
      pageSize: SEARCH_PAGE_SIZE,
    });
  } catch {
    return [];
  }
};
